// src/encounter/services/use-item-lots.ts
// GET /api/Inventory/Items/{stockItemId}/Lots → ItemLot[] for the vaccine lot picker
// (lot-aware deduction, PROD-1227; BE Inventory #36, deployed on dev).
//
// On-hand lots for the vaccine at the org's default vaccination store, §6-ordered
// (earliest-expiry first). The picker shows them, flags Suggested/Expired, and auto-fills
// expiry on select. READ-ONLY — deduction happens server-side on sign-lock, never here.
// `organizationId` header is injected by api-client from the authenticated user.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ItemLot } from '@/types/inventory-api';

export function useItemLots(stockItemId: string | null | undefined) {
  const id = stockItemId?.trim();
  return useQuery<ItemLot[]>({
    queryKey: ['inventory', 'item-lots', id],
    enabled: !!id,
    queryFn: async ({ signal }) => {
      const res = await get<ItemLot[]>(
        `/api/Inventory/Items/${encodeURIComponent(id as string)}/Lots`,
        { signal },
      );
      return res ?? [];
    },
  });
}

/**
 * By-vaccine-code variant — the clinical vaccine field selects a CVX code, not an inventory
 * StockItemId, so the lot picker resolves lots through this (BE resolves Code/ClinicalRef →
 * StockItem → lots; Inventory #39). Unknown/blank code → []. Use this from the administration popup.
 */
export function useLotsByVaccineCode(vaccineCode: string | null | undefined) {
  const code = vaccineCode?.trim();
  return useQuery<ItemLot[]>({
    queryKey: ['inventory', 'lots-by-code', code],
    enabled: !!code,
    queryFn: async ({ signal }) => {
      const res = await get<ItemLot[]>(
        `/api/Inventory/Items/ByCode/${encodeURIComponent(code as string)}/Lots`,
        { signal },
      );
      return res ?? [];
    },
  });
}
